import type { RefObject } from "react";
import { Braces, AtSign } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/atoms/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/popover";
import { DropdownPanelListRow } from "@/components/molecules/dropdownPanel/dropdownListRow";
import {
  RTE_TOOLTIP_INSERT_VARIABLE,
  RTE_SHORTCUT_INSERT_DIRECTIVE_VARIABLE,
} from "@/constants/uiCopy/richTextEditor";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { ToolbarButton } from "./richTextEditorToolbarButton";
import { getRteShortcutKeys } from "./richTextEditorToolbarShortcuts";
import { RteTooltipWithShortcut } from "./richTextEditorToolbarTooltipContent";

type DirectiveItem = { key: string; label: string };

export const RichTextEditorToolbarVariablePopover = ({
  modKeyLabel,
  open,
  onOpenChange,
  variables,
  onInsertVariable,
}: {
  modKeyLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variables: Variables[];
  onInsertVariable: (variableName: string) => void;
}) => {
  const keys = getRteShortcutKeys(modKeyLabel);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <ToolbarButton
              disabled={variables.length === 0}
              aria-label="Insert variable"
            >
              <Braces className="size-4" />
            </ToolbarButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <RteTooltipWithShortcut
            label={RTE_TOOLTIP_INSERT_VARIABLE}
            keys={keys.variable}
          />
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        dropdownPanel
        dropdownPanelScrollable
        className="w-auto max-w-[320px]"
        align="start"
        sideOffset={4}
      >
        <ul className="list-none p-0" role="listbox">
          {variables.map((v) => (
            <DropdownPanelListRow
              key={v.name}
              role="option"
              aria-label={`@${v.name}`}
              onClick={() => onInsertVariable(v.name)}
            >
              @{v.name}
            </DropdownPanelListRow>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export const RichTextEditorToolbarDirectivePopover = ({
  modKeyLabel,
  open,
  onOpenChange,
  availableDirectives,
  onInsertDirective,
  justInsertedDirectiveRef,
}: {
  modKeyLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableDirectives: DirectiveItem[];
  onInsertDirective: (directiveKey: string) => void;
  justInsertedDirectiveRef: RefObject<boolean>;
}) => {
  const keys = getRteShortcutKeys(modKeyLabel);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <ToolbarButton
              disabled={availableDirectives.length === 0}
              aria-label="Insert directive variable"
            >
              <AtSign className="size-4" />
            </ToolbarButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <RteTooltipWithShortcut
            label={RTE_SHORTCUT_INSERT_DIRECTIVE_VARIABLE}
            keys={keys.directive}
          />
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        dropdownPanel
        dropdownPanelScrollable
        className="w-auto max-w-[320px]"
        align="start"
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          if (justInsertedDirectiveRef.current) {
            e.preventDefault();
            justInsertedDirectiveRef.current = false;
          }
        }}
      >
        <ul className="list-none p-0" role="listbox">
          {availableDirectives.map((d) => (
            <DropdownPanelListRow
              key={d.key}
              role="option"
              aria-label={d.label}
              onClick={() => onInsertDirective(d.key)}
            >
              {d.label}
            </DropdownPanelListRow>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
